import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import {
  IconAnchor,
  IconCalendarEvent,
  IconCertificate,
  IconClipboardHeart,
  IconClockHour3,
  IconMinusVertical,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconCircleFilled,
} from '@tabler/icons-react';
import useGetTripTransfer from '../../../hooks/useGetTripTransfer';
import useDiveSites from '../../../hooks/useDiveSites';
import useTripSkills from '../../../hooks/useTripSkills';
import useTripDetails from '../../../hooks/useTripDetailsQuery';
import {
  formatDateRange,
  formatInputDate,
  formatTime,
} from '../../../common/utilities/dateFormatter';

interface TripItineraryContentProps {
  tripId: number;
  enabled: boolean;
}

const TripItineraryContent: React.FC<TripItineraryContentProps> = ({
  tripId,
  enabled,
}) => {
  const shouldFetch = enabled && tripId > 0;

  const { tripTransfer, tripTransferLoading } = useGetTripTransfer(
    tripId,
    shouldFetch,
  );
  const { diveSites, isLoading: diveSitesLoading } = useDiveSites(
    tripId,
    shouldFetch,
  );
  const { tripSkills, isLoading: tripSkillsLoading } = useTripSkills(
    tripId,
    shouldFetch,
  );
  const { tripDetails, isLoading: tripDetailLoading } = useTripDetails(
    tripId,
    shouldFetch,
  );

  return (
    <div className="text-neutral-800">
      <div className="flex flex-col">
        <span className="text-2xl text-neutral-800 font-semibold">
          {tripDetailLoading ? (
            <Skeleton width={200} />
          ) : (
            tripDetails?.routeName || 'Unknown Route'
          )}
        </span>
        {tripDetailLoading ? (
          <Skeleton width="100%" height={30} className="mt-2" />
        ) : (
          <span className="flex flex-wrap items-center text-neutral-500 mt-2">
            {formatDateRange(
              tripDetails?.embarkationDate,
              tripDetails?.disembarkationDate,
              true,
            )}
            <IconMinusVertical size={20} />
            {tripDetails?.diveCount ? `${tripDetails.diveCount} dives` : 'N/A'}
            <IconMinusVertical size={20} />
            {tripDetails?.boatName || 'Unknown Boat'}
          </span>
        )}
      </div>

      <div className="mt-12">
        <span className="text-neutral-800 font-semibold">Requirement</span>
        {tripSkillsLoading ? (
          <Skeleton width="100%" height={30} />
        ) : tripSkills.length ? (
          tripSkills.map(skill => (
            <div className="flex items-start mt-4" key={skill.skillId}>
              <IconCertificate size={20} />
              <div className="flex flex-col items-start ml-2">
                <span className="text-neutral-800">{skill.skillName}</span>
              </div>
            </div>
          ))
        ) : null}
        <div className="mt-4 flex items-center">
          <IconClipboardHeart size={20} />
          <span className="ml-2">Dive and travel insurance</span>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          <span className="text-neutral-800 font-semibold">Departure</span>
          {tripTransferLoading ? (
            <>
              <Skeleton width="80%" />
              <Skeleton width="60%" />
              <Skeleton width="70%" />
              <Skeleton width="90%" />
            </>
          ) : (
            <>
              <div className="flex mt-4">
                <IconCalendarEvent size={20} />
                <span className="ml-2">
                  {formatInputDate(tripDetails?.embarkationDate) || 'N/A'}
                </span>
              </div>
              <div className="flex mt-4">
                <IconClockHour3 size={20} />
                <span className="ml-2">
                  {formatTime(tripTransfer?.tripTransfer.embarkationTime) ||
                    'N/A'}
                </span>
              </div>
              <div className="flex mt-4">
                <IconAnchor size={20} />
                <span className="ml-2">
                  {tripTransfer?.tripTransfer.departurePierName || 'Pier'}
                </span>
              </div>
              <div className="flex mt-4">
                <IconPlaneDeparture size={20} />
                <span className="ml-2">
                  {tripTransfer?.tripTransfer.departureAirportName} (
                  {tripTransfer?.tripTransfer.departureAirportCode})
                </span>
              </div>
            </>
          )}
        </div>

        <div>
          <span className="text-neutral-800 font-semibold">Return</span>
          {tripTransferLoading ? (
            <>
              <Skeleton width="80%" />
              <Skeleton width="60%" />
              <Skeleton width="70%" />
              <Skeleton width="90%" />
            </>
          ) : (
            <>
              <div className="flex mt-4">
                <IconCalendarEvent size={20} />
                <span className="ml-2">
                  {formatInputDate(tripDetails?.disembarkationDate) || 'N/A'}
                </span>
              </div>
              <div className="flex mt-4">
                <IconClockHour3 size={20} />
                <span className="ml-2">
                  {formatTime(tripTransfer?.tripTransfer.disembarkationTime) ||
                    'N/A'}
                </span>
              </div>
              <div className="flex mt-4">
                <IconAnchor size={20} />
                <span className="ml-2">
                  {tripTransfer?.tripTransfer.returnPierName || 'Pier'}
                </span>
              </div>
              <div className="flex mt-4">
                <IconPlaneArrival size={20} />
                <span className="ml-2">
                  {tripTransfer?.tripTransfer.returnAirportName} (
                  {tripTransfer?.tripTransfer.returnAirportCode})
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {!diveSitesLoading && diveSites.length > 0 && (
        <div className="mt-12">
          <span className="text-neutral-800 font-semibold">
            Highlight dive sites
          </span>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {diveSites.map(site => (
              <div className="flex items-center" key={site.diveSiteId}>
                <IconCircleFilled size={16} />
                <span className="ml-2">{site.siteName}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TripItineraryContent;
